import { useEffect, useState, useCallback } from "react";
import { Flame } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Focus Streak badge — shows consecutive UTC days the user has completed at
 * least one Micro-Chunker task. Pinging is driven by the MicroChunker
 * component when a user checks off a task; this component just polls on
 * mount and listens for a custom `oak:streak-ping` event so the header
 * refreshes instantly without a page reload.
 */
export default function FocusStreak() {
  const { user } = useAuth();
  const [data, setData] = useState({ streak: 0, pinged_today: false });

  const refresh = useCallback(() => {
    if (!user) return;
    api.get("/streak")
      .then(({ data: d }) => setData(d || { streak: 0 }))
      .catch(() => {});
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const h = () => refresh();
    window.addEventListener("oak:streak-ping", h);
    return () => window.removeEventListener("oak:streak-ping", h);
  }, [refresh]);

  if (!user || !data.streak) return null;

  const active = data.pinged_today;
  return (
    <div
      className={`hidden sm:inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-3 py-1 text-sm transition glass !rounded-full ${
        active
          ? "border-[#FF7B00]/40 bg-[#FF7B00]/10 glow-mint"
          : "border-white/10"
      }`}
      title={`Focus Streak · ${data.streak} consecutive day${data.streak === 1 ? "" : "s"}${active ? " (extended today)" : " — chunk a task today to keep it alive"}`}
      data-testid="header-focus-streak"
    >
      <Flame size={13} className={active ? "text-[#ffb067]" : "text-white/60"} />
      <span className="font-display font-semibold text-sm tabular-nums">{data.streak}</span>
      <span className="text-white/50 text-[10px] uppercase tracking-wider hidden md:inline">
        streak
      </span>
    </div>
  );
}
